import { useMemo, useRef } from 'react';

import { themeObjectToString } from '@atlaskit/tokens';
import type { ThemeState } from '@atlaskit/tokens';
import {
	EMBEDDED_CONFLUENCE_MODE,
	EMBEDDED_CONFLUENCE_CONTENT_TYPE,
	type TemplateType,
} from './index';
import { getAllowedFeatures } from '../features';
import type { PageCommonProps } from './PageCommonProps';
import type { ContentProps } from '../content';
import { useIntl } from 'react-intl';
import {
	type EnableRedirectionAllowedFeatureType,
	getCrossSiteTenantIdFromAllowedFeature,
	isRedirectionEnabled,
} from './utils/allowedFeaturesUtils';
import { useUniqueKey } from '../unique-key-provider/UniqueKeyProvider';

type BaseEmbeddedQueryParams = {
	draftShareId?: string;
	embeddedConfluenceSource?: string;
	inEditorTemplatesPanel?: string;
	initialAllowedFeatures?: string;
	insertTemplateWithKey?: TemplateType;
	isCrossDomain?: boolean;
	locale?: string;
	NO_SSR?: string;
	parentProduct?: string;
	previewPanels?: string;
	themeState?: string;
	uniqueKey?: string;
};

type EmbeddedUrlQuery = Partial<
	Pick<PageCommonProps, 'parentProductContentContainerId' | 'parentProductContentId'>
> &
	BaseEmbeddedQueryParams;

export type UrlProps = Omit<ContentProps, 'children'>;

export const themeStateObjectToQueryString = (themeState: Partial<ThemeState>): string => {
	const themeString = themeObjectToString(themeState);
	return encodeURIComponent(themeString);
};

const STARGATE_CONFLUENCE_PROXY = '/gateway/api/ex/confluence/';

const nonAllFeatures = ['modern-header', 'disable-help-button', 'disable-title-toolbar'];

export const useUrl = ({
	allowedFeatures,
	contentId,
	contentType,
	contentConfiguration,
	draftShareId,
	hash,
	hostname,
	mode,
	parentProduct,
	parentProductContentContainerId,
	parentProductContentId,
	previewPanels,
	protocol,
	spaceKey,
	template,
	themeState,
	embeddedConfluenceSource,
}: UrlProps):
	| {
			embeddedConfluenceUrl: null;
			standaloneConfluenceUrl: null;
	  }
	| {
			embeddedConfluenceUrl: string;
			standaloneConfluenceUrl: string;
	  } => {
	const initialAllowedFeaturesRef = useRef<string | undefined>(undefined);

	// Keep `initialAllowedFeatures` to only contain the initial allowed feature values for cc-frontend SSR rendering
	// Store this value using `useRef` to avoid the iframe url gets updated when `allowedFeatures` React props updated by parent products.
	// If allowedFeatures is equal to 'all' for view mode it must be converted to an array so that 'edit' can be removed.
	const features = useMemo(
		() => (allowedFeatures === 'all' ? getAllowedFeatures({ contentType, mode }) : allowedFeatures),
		[allowedFeatures, contentType, mode],
	);

	if (!initialAllowedFeaturesRef.current) {
		initialAllowedFeaturesRef.current = Array.isArray(features)
			? features
					.map((feature) => {
						if (typeof feature !== 'string') {
							return JSON.stringify(feature);
						} else {
							return feature;
						}
					})
					// 'edit' can not be included as an initialAllowedFeature because it requires the navigation policy, which does not work with SSR.
					// 'enable-redirection' should not be included in initial allowed features unless a parent has passed this in as an allowedFeature in the correct format.
					.filter((feature) => feature !== 'edit' && feature !== 'enable-redirection')
					// 'modern-header' should not be included in initial allowed features when allowedFeatures is 'all'
					.filter((feature) => !nonAllFeatures.includes(feature))
					.join('.')
			: features;
	}

	// check whether re-direction is enabled
	const enableRedirectionFeature =
		features && features !== 'all'
			? features.find((feature) => isRedirectionEnabled(feature))
			: undefined;
	const crossSiteTenantId = enableRedirectionFeature
		? getCrossSiteTenantIdFromAllowedFeature(
				enableRedirectionFeature as EnableRedirectionAllowedFeatureType,
			)
		: undefined;

	const { locale } = useIntl();

	const { uniqueKey } = useUniqueKey();

	const queryParams = useMemo(() => {
		const query: EmbeddedUrlQuery = {};

		if (parentProduct) {
			query.parentProduct = parentProduct;
		}

		if (parentProductContentContainerId) {
			query.parentProductContentContainerId = parentProductContentContainerId;
		}

		if (parentProductContentId) {
			query.parentProductContentId = parentProductContentId;
		}

		if (initialAllowedFeaturesRef.current !== undefined) {
			query.initialAllowedFeatures = initialAllowedFeaturesRef.current;
		}

		if (template) {
			query.insertTemplateWithKey = template;
		}

		if (themeState) {
			query.themeState = themeStateObjectToQueryString(themeState);
		}

		if (draftShareId) {
			query.draftShareId = draftShareId;
		}

		if (embeddedConfluenceSource) {
			query.embeddedConfluenceSource = embeddedConfluenceSource;
		}

		if (previewPanels) {
			query.previewPanels = previewPanels;
		}

		if (hostname && hostname !== window.location.host) {
			query.isCrossDomain = true;
		}

		if (uniqueKey) {
			query.uniqueKey = uniqueKey;
		}

		query.locale = locale;

		const { isCrossDomain, ...restQuery } = query;

		return `?${new URLSearchParams({
			...(isCrossDomain ? { isCrossDomain: `${isCrossDomain}` } : {}),
			...restQuery,
			...contentConfiguration?.queryParams,
		}).toString()}`;
	}, [
		contentConfiguration,
		draftShareId,
		locale,
		parentProduct,
		parentProductContentContainerId,
		parentProductContentId,
		previewPanels,
		themeState,
		hostname,
		embeddedConfluenceSource,
		template,
		uniqueKey,
	]);

	return useMemo(
		() => ({
			...constructUrl(mode, {
				contentId,
				contentType,
				hash,
				hostname,
				protocol,
				spaceKey,
				queryParams,
				crossSiteTenantId,
			}),
		}),
		[
			contentId,
			contentType,
			hash,
			hostname,
			mode,
			protocol,
			spaceKey,
			queryParams,
			crossSiteTenantId,
		],
	);
};

type ConstructUrlProps = Pick<
	UrlProps,
	'spaceKey' | 'contentId' | 'contentType' | 'protocol' | 'hostname' | 'hash' | 'themeState'
> & {
	crossSiteTenantId?: string;
	queryParams: string;
};

const CONTEXT_PATH = '/wiki/spaces';

const invalidUrls = {
	standaloneConfluenceUrl: null,
	embeddedConfluenceUrl: null,
};

function constructUrl(
	mode: EMBEDDED_CONFLUENCE_MODE | undefined = EMBEDDED_CONFLUENCE_MODE.VIEW_MODE,
	{
		contentId,
		contentType,
		hash = '',
		hostname,
		protocol,
		spaceKey,
		queryParams,
		crossSiteTenantId,
	}: ConstructUrlProps,
) {
	if (contentType !== EMBEDDED_CONFLUENCE_CONTENT_TYPE.PAGE && !contentId) {
		/* eslint-disable-next-line no-console */
		console.error('Content ID is undefined');
		return invalidUrls;
	}
	if (!spaceKey) {
		/* eslint-disable-next-line no-console */
		console.error('Space key is undefined');
		return invalidUrls;
	}

	if (/^\d+$/.test(spaceKey)) {
		/* eslint-disable-next-line no-console */
		console.warn(
			'A numeric "spaceKey" is passed into the Embedded Confluence component. Verify that it is the correct "spaceKey" and not the "spaceId." Disregard this warning if it is correct.',
		);
	}

	const prefix = `${protocol || window.location.protocol}//${
		hostname || window.location.host
	}${CONTEXT_PATH}`;

	const prefixWithStargate = crossSiteTenantId
		? `${protocol || window.location.protocol}//${
				window.location.host //take the parent site because want don't want it to be cross-site EP
			}${STARGATE_CONFLUENCE_PROXY}${crossSiteTenantId}${CONTEXT_PATH}`
		: undefined;

	let base: string;

	const pageUrl: Record<EMBEDDED_CONFLUENCE_MODE, string> = {
		[EMBEDDED_CONFLUENCE_MODE.VIEW_MODE]: contentId
			? `${spaceKey}/pages/${contentId}`
			: `${spaceKey}/overview`,
		[EMBEDDED_CONFLUENCE_MODE.EDIT_MODE]: `${spaceKey}/pages/edit-embed/${contentId}`,
	};

	const blogpostUrl: Record<EMBEDDED_CONFLUENCE_MODE, string> = {
		[EMBEDDED_CONFLUENCE_MODE.VIEW_MODE]: `${spaceKey}/pages/${contentId}`,
		[EMBEDDED_CONFLUENCE_MODE.EDIT_MODE]: `${spaceKey}/pages/edit-embed/${contentId}`,
	};

	const whiteboardUrl: Record<EMBEDDED_CONFLUENCE_MODE, string> = {
		[EMBEDDED_CONFLUENCE_MODE.VIEW_MODE]: `${spaceKey}/whiteboard/${contentId}`,
		[EMBEDDED_CONFLUENCE_MODE.EDIT_MODE]: `${spaceKey}/whiteboard/${contentId}`,
	};

	const contentTypeUrl: Record<
		EMBEDDED_CONFLUENCE_CONTENT_TYPE,
		Record<EMBEDDED_CONFLUENCE_MODE, string>
	> = {
		[EMBEDDED_CONFLUENCE_CONTENT_TYPE.PAGE]: pageUrl,
		[EMBEDDED_CONFLUENCE_CONTENT_TYPE.BLOGPOST]: blogpostUrl,
		[EMBEDDED_CONFLUENCE_CONTENT_TYPE.WHITEBOARD]: whiteboardUrl,
	};

	base = contentTypeUrl[contentType]?.[mode];

	if (!base) {
		/* eslint-disable-next-line no-console */
		console.error('URL is not yet supported');
		return invalidUrls;
	}

	const standaloneConfluenceUrl = `${prefix}/${base}${hash}`;
	const embeddedConfluenceUrl = `${prefixWithStargate ?? prefix}/${base}${queryParams}${hash}`;

	return { standaloneConfluenceUrl, embeddedConfluenceUrl };
}
